import React from 'react';
import classNames from 'classnames';
import {ImageLoadingBehaviorOptions, ImageResizeOptions, WowImage} from 'wix-ui-tpa/cssVars';
import * as Icons from '@wix/wixstores-client-common-components/dist/es/src/icons/dist';
import {HoverType, MediaFrameMediaType, ProductType} from '../../../../types/galleryTypes';
import s from './ImageUiTpaWrapper.scss';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {classes} from './ImageUiTpaWrapper.st.css';
import {DefaultProductImage} from '@wix/wixstores-client-common-components/dist/es/src/DefaultProductImage/DefaultProductImage';
import {DEFAULT_GALLERY_IMAGE_SIZE, OLD_DEFAULT_GALLERY_IMAGE_SIZE, Experiments} from '../../../../constants';
import {useExperiments} from '@wix/yoshi-flow-editor';

export enum ImageUiTpaWrapperDataHook {
  Wrapper = 'ImageUiTpaWrapperDataHook.Wrapper',
  Media = 'ImageUiTpaWrapperDataHook.Media',
  DefaultImage = 'ImageUiTpaWrapperDataHook.DefaultImage',
}

export interface ImageUiTpaWrapperProps extends IGalleryGlobalProps {
  product: any;
  wrapperClassName?: string;
  imageClassName?: string;
  mediaItemIndex?: number;
  resize?: ImageResizeOptions;
  containerAspectRatio: number;
  hoverType: HoverType;
}
export const getWowImageDataHook = (index: number) => `${ImageUiTpaWrapperDataHook.Media}_${index}`;
export const getImageWrapperDataHook = (index: number) => `${ImageUiTpaWrapperDataHook.Wrapper}_${index}`;

const iconsByFileType = (product) => {
  const fileType = product?.digitalProductFileItems[0]?.fileType;
  const size = OLD_DEFAULT_GALLERY_IMAGE_SIZE;

  const icons = {
    [MediaFrameMediaType.SECURE_DOCUMENT]: <Icons.DocumentDigitalProductDefaultImage size={size} />,
    [MediaFrameMediaType.SECURE_PICTURE]: <Icons.PictureDigitalProductDefaultImage size={size} />,
    [MediaFrameMediaType.SECURE_VIDEO]: <Icons.VideoDigitalProductDefaultImage size={size} />,
    [MediaFrameMediaType.SECURE_MUSIC]: <Icons.MusicDigitalProductDefaultImage size={size} />,
    [MediaFrameMediaType.SECURE_ARCHIVE]: <Icons.DocumentDigitalProductDefaultImage size={size} />,
  };

  return icons[fileType] || icons[MediaFrameMediaType.SECURE_DOCUMENT];
};

const isDigitalProduct = (product) => product.productType === ProductType.DIGITAL;

const oldDefaultImage = (product) => {
  if (isDigitalProduct(product)) {
    return iconsByFileType(product);
  } else {
    return <Icons.PhysicalProductDefaultImage size={OLD_DEFAULT_GALLERY_IMAGE_SIZE} />;
  }
};

export const ImageUiTpaWrapper = ({
  product,
  wrapperClassName,
  mediaItemIndex,
  imageClassName,
  resize,
  containerAspectRatio,
  globals: {isSEO},
}: ImageUiTpaWrapperProps) => {
  const mediaItem = product.media.length > mediaItemIndex && product.media[mediaItemIndex];
  const alt = mediaItem.altText || product.name;
  const isContain = resize === ImageResizeOptions.contain;
  const {experiments} = useExperiments();
  const shouldUseNewDefaultProductImage = experiments.enabled(Experiments.ShouldUseNewDefaultProductImage);

  if (mediaItem) {
    return (
      <div
        className={classNames(wrapperClassName, s.imageContainer)}
        data-hook={getImageWrapperDataHook(mediaItemIndex)}>
        <WowImage
          data-hook={getWowImageDataHook(mediaItemIndex)}
          src={mediaItem.url}
          className={classNames(classes.root, imageClassName)}
          stretchImage={!isContain}
          sourceWidth={mediaItem.width}
          sourceHeight={mediaItem.height}
          alt={alt}
          fluid={true}
          loadingBehavior={ImageLoadingBehaviorOptions.none}
          resize={resize}
          shouldUseLQIP={true}
          containerAspectRatio={containerAspectRatio}
          forceImageContain={isContain}
          isSEOBot={isSEO}
        />
      </div>
    );
  } else if (shouldUseNewDefaultProductImage) {
    return (
      <DefaultProductImage
        imageSize={DEFAULT_GALLERY_IMAGE_SIZE}
        dataHook={ImageUiTpaWrapperDataHook.DefaultImage}
        imageContainerDimensions={{height: mediaItem.height, width: mediaItem.width}}
        className={s.defaultImage}
      />
    );
  } else {
    return (
      <div
        data-hook={ImageUiTpaWrapperDataHook.DefaultImage}
        className={s.oldDefaultImage}
        style={{width: mediaItem.width, height: mediaItem.height}}>
        {oldDefaultImage(product)}
      </div>
    );
  }
};
